import { useEffect, useRef, useState } from "react";
import { voiceService } from "../../services/voiceService";

// Ditado por voz do molde dos assistentes, no motor que a clínica escolheu (RN-554d): `webspeech` (navegador; no Chrome o
// áudio vai ao Google e o Firefox não tem) ou `whisper` (grava aqui e transcreve no VPS; o áudio não sai do servidor).
// Fica gravando até a pessoa clicar de novo ou até 15 s sem fala, somando ao que já foi transcrito.

// No modo conversa (RN-570), `onPause` recebe o texto quando a pessoa para de falar por PAUSE_MS: é o envio sem clique.
// No Whisper a pausa é medida no próprio áudio (nível do microfone). Enquanto a pessoa fala, o áudio é cortado em pedaços
// numa micro-pausa entre palavras (de CHUNK_MIN_MS a CHUNK_MAX_MS) e cada pedaço já vai transcrito: quando ela para,
// falta só o último, em vez da fala inteira. Os textos entram na ordem em que foram falados.
const SILENCE_MS = 15000;
const PAUSE_MS = 1800;
const CHUNK_MIN_MS = 3500;
const CHUNK_MAX_MS = 9000;
const GAP_MS = 350;
const TICK_MS = 100;
const MIN_LEVEL = 0.015;

export type DictationEngine = "webspeech" | "whisper";

const rms = (data: Float32Array) => {
  let sum = 0;
  for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
  return Math.sqrt(sum / data.length);
};

export const useDictation = (
  draft: string,
  setDraft: (text: string) => void,
  onError: (message: string) => void,
  onPause?: (text: string) => void,
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const SpeechRec: any = typeof window !== "undefined" ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition : null;
  const canRecord = typeof window !== "undefined" && typeof MediaRecorder !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  const [engine, setEngine] = useState<DictationEngine | null>(null);
  const engineRef = useRef<DictationEngine | null>(null);

  const recRef = useRef<{ stop: () => void; start: () => void } | null>(null);
  const wantRef = useRef(false);
  const silenceRef = useRef<number | null>(null);
  const baseRef = useRef("");
  const textRef = useRef("");
  const pauseRef = useRef<number | null>(null);
  const onPauseRef = useRef(onPause);
  onPauseRef.current = onPause;
  const setDraftRef = useRef(setDraft);
  setDraftRef.current = setDraft;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  // Whisper: microfone, medidor de nível e o gravador do trecho atual
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const tickRef = useRef<number | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  // Descartar = o trecho em gravação não vira texto (envio manual, saída do modo conversa)
  const discardRef = useRef(false);
  // Pedaços em transcrição: as respostas chegam fora de ordem, a fila garante a ordem da fala
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingRef = useRef(0);

  useEffect(() => {
    let alive = true;
    voiceService.getSettings()
      .then((s) => { if (alive) { engineRef.current = s.engine; setEngine(s.engine); } })
      .catch(() => { if (alive) { engineRef.current = "webspeech"; setEngine("webspeech"); } });
    return () => { alive = false; };
  }, []);

  const supported = engine === "whisper" ? canRecord : !!SpeechRec;

  const releaseMic = () => {
    if (tickRef.current) window.clearInterval(tickRef.current);
    tickRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    audioCtxRef.current?.close().catch(() => undefined);
    audioCtxRef.current = null;
  };

  const clearTimers = () => {
    if (silenceRef.current) window.clearTimeout(silenceRef.current);
    silenceRef.current = null;
    if (pauseRef.current) window.clearTimeout(pauseRef.current);
    pauseRef.current = null;
  };

  const halt = (discard: boolean) => {
    wantRef.current = false;
    clearTimers();
    recRef.current?.stop();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      discardRef.current = discard;
      recorder.stop();
    } else {
      releaseMic();
    }
    setListening(false);
  };
  const stop = () => halt(false);
  const cancel = () => halt(true);

  const armSilence = () => {
    if (silenceRef.current) window.clearTimeout(silenceRef.current);
    silenceRef.current = window.setTimeout(stop, SILENCE_MS);
  };
  const armPause = () => {
    if (!onPauseRef.current) return;
    if (pauseRef.current) window.clearTimeout(pauseRef.current);
    pauseRef.current = window.setTimeout(() => {
      const text = textRef.current.trim();
      if (wantRef.current && text) onPauseRef.current?.(text);
    }, PAUSE_MS);
  };
  useEffect(() => () => {
    wantRef.current = false;
    discardRef.current = true;
    recRef.current?.stop();
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    releaseMic();
    if (silenceRef.current) window.clearTimeout(silenceRef.current);
    if (pauseRef.current) window.clearTimeout(pauseRef.current);
  }, []);

  const toggle = () => (listening ? stop() : start(draft));

  const startBrowser = async (initial: string) => {
    if (!SpeechRec) { onErrorRef.current("Ditado por voz não é suportado neste navegador. Use Chrome, Edge ou Safari."); return; }
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      }
      const rec = new SpeechRec();
      rec.lang = "pt-BR"; rec.interimResults = true; rec.continuous = true;
      baseRef.current = initial.trim() ? `${initial.trim()} ` : "";
      textRef.current = baseRef.current;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rec.onresult = (e: any) => {
        let t = "";
        for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
        textRef.current = baseRef.current + t.trim();
        setDraftRef.current(textRef.current);
        armSilence();
        armPause();
      };
      rec.onend = () => {
        if (wantRef.current) {
          // Pausa encerrou a sessão do navegador: reabre sem perder o que foi dito
          baseRef.current = textRef.current.trim() ? `${textRef.current.trim()} ` : "";
          try { rec.start(); } catch { stop(); }
        } else {
          setListening(false);
        }
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rec.onerror = (e: any) => { if (e?.error !== "no-speech" && e?.error !== "aborted") stop(); };
      recRef.current = rec; wantRef.current = true; setListening(true); armSilence(); rec.start();
    } catch {
      stop(); onErrorRef.current("Permita o acesso ao microfone no navegador para ditar por voz.");
    }
  };

  // Manda o pedaço já, mas só soma o texto depois dos anteriores: a fala sai na ordem mesmo que as respostas não venham
  const sendChunk = (blob: Blob) => {
    pendingRef.current += 1;
    setTranscribing(true);
    const request = voiceService.transcribe(blob).then((t) => t.trim()).catch((e) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onErrorRef.current((e as any)?.response?.data?.error || "Não foi possível transcrever este trecho.");
      return "";
    });
    queueRef.current = queueRef.current
      .then(() => request)
      .then((text) => {
        if (!text) return;
        textRef.current = `${textRef.current}${text} `;
        setDraftRef.current(textRef.current.trim());
      })
      .finally(() => {
        pendingRef.current -= 1;
        if (pendingRef.current === 0) setTranscribing(false);
      });
  };

  // Uma escuta = do clique (ou da reabertura do modo conversa) até parar. Dentro dela, um gravador por pedaço.
  const startTurn = () => {
    const stream = streamRef.current;
    const ctx = audioCtxRef.current;
    if (!stream || !ctx) return;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const samples = new Float32Array(analyser.fftSize);

    let heardTurn = false;
    let lastVoice = Date.now();
    let floor = MIN_LEVEL / 3;
    let chunkStart = Date.now();

    const finishTurn = (discard: boolean) => {
      releaseMic();
      const conversation = onPauseRef.current;
      queueRef.current.then(() => {
        const text = textRef.current.trim();
        if (conversation && !discard && text) conversation(text);
      });
    };

    const openChunk = () => {
      const chunks: Blob[] = [];
      const recorder = new MediaRecorder(stream);
      const heardRef = { current: false };
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        // O último gravador da escuta parado com a escuta encerrada fecha a vez; os anteriores só entregam o pedaço
        const final = recorderRef.current === recorder && !wantRef.current;
        const discard = final && discardRef.current;
        if (final) discardRef.current = false;
        if (heardRef.current && !discard && chunks.length > 0) {
          sendChunk(new Blob(chunks, { type: recorder.mimeType || "audio/webm" }));
        }
        if (final) finishTurn(discard);
      };
      recorderRef.current = recorder;
      chunkStart = Date.now();
      recorder.start();
      return heardRef;
    };

    let heardRef = openChunk();
    tickRef.current = window.setInterval(() => {
      analyser.getFloatTimeDomainData(samples);
      const level = rms(samples);
      const threshold = Math.max(MIN_LEVEL, floor * 3);
      const now = Date.now();
      if (level > threshold) {
        heardTurn = true;
        heardRef.current = true;
        lastVoice = now;
      } else {
        floor = floor * 0.95 + level * 0.05;
      }

      const chunkAge = now - chunkStart;
      if (onPauseRef.current && heardTurn && now - lastVoice >= PAUSE_MS) {
        // No modo conversa a pausa encerra a escuta: a resposta é falada e o modo reabre o microfone depois
        wantRef.current = false;
        setListening(false);
        recorderRef.current?.stop();
      } else if (now - lastVoice >= SILENCE_MS) {
        stop();
      } else if (heardRef.current && ((chunkAge >= CHUNK_MIN_MS && now - lastVoice >= GAP_MS) || chunkAge >= CHUNK_MAX_MS)) {
        const previous = recorderRef.current;
        heardRef = openChunk();
        previous?.stop();
      }
    }, TICK_MS);
  };

  const startWhisper = async (initial: string) => {
    if (!canRecord) { onErrorRef.current("Este navegador não consegue gravar áudio."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      audioCtxRef.current = new AudioContext();
      textRef.current = initial.trim() ? `${initial.trim()} ` : "";
      wantRef.current = true;
      setListening(true);
      startTurn();
    } catch {
      releaseMic();
      setListening(false);
      wantRef.current = false;
      onErrorRef.current("Permita o acesso ao microfone no navegador para ditar por voz.");
    }
  };

  /** Começa a ouvir a partir de `initial` (o modo conversa reabre com o campo vazio, não com o rascunho antigo). */
  const start = async (initial: string) => {
    if (wantRef.current || streamRef.current) return;
    if (engineRef.current === "whisper") await startWhisper(initial);
    else await startBrowser(initial);
  };

  return { engine, supported, listening, transcribing, toggle, start, stop, cancel };
};
